// src/ProtectedRoute.js
import React, { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from './axiosConfig';

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** Cache whoami success for 10s to avoid storms */
const WHOAMI_CACHE_MS = 10000;

let whoamiState = {
  ts: 0,
  ok: false,
  inflight: null,
};

function sessionWhoamiFresh() {
  try {
    const ts = Number(sessionStorage.getItem('whoami_ok_ts') || 0);
    return ts && Date.now() - ts < WHOAMI_CACHE_MS;
  } catch {
    return false;
  }
}

function markSessionWhoamiOK() {
  try {
    sessionStorage.setItem('whoami_ok_ts', String(Date.now()));
  } catch {}
}

function getWhoAmIOnce(signal) {
  const now = Date.now();

  // If we recently confirmed auth (from session storage), trust it
  if (sessionWhoamiFresh()) return Promise.resolve(true);

  // Fresh OK cache ? resolve immediately
  if (whoamiState.ok && now - whoamiState.ts < WHOAMI_CACHE_MS) {
    return Promise.resolve(true);
  }

  // Reuse inflight promise if any
  if (whoamiState.inflight) return whoamiState.inflight;

  // Start a new single-flight
  whoamiState.inflight = axios
    .get('auth/whoami/', { timeout: 4000, signal })
    .then(() => {
      whoamiState.ts = Date.now();
      whoamiState.ok = true;
      markSessionWhoamiOK();
      return true;
    })
    .catch((err) => {
      whoamiState.ts = Date.now();
      whoamiState.ok = false;
      throw err;
    })
    .finally(() => {
      whoamiState.inflight = null;
    });

  return whoamiState.inflight;
}

export default function ProtectedRoute({ children }) {
  // Determine once per render if we're on the public department portal
  const isDeptPortal =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/department-messages');

  // For dept portal, treat as already "authed" from this component's POV
  const [state, setState] = useState(isDeptPortal ? 'authed' : 'pending');
  const startedRef = useRef(false);

  useEffect(() => {
    // Department messaging portal: skip whoami entirely
    if (isDeptPortal) {
      setState('authed');
      return;
    }

    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    const ctrl = new AbortController();

    const run = async () => {
      // If we have a fresh session cache, accept immediately
      if (sessionWhoamiFresh()) {
        if (!cancelled) setState('authed');
        return;
      }

      // After OTP we set this once ? be extra patient on first guard
      let justLoggedIn = false;
      try {
        justLoggedIn = sessionStorage.getItem('justLoggedIn') === '1';
        if (justLoggedIn) sessionStorage.removeItem('justLoggedIn');
      } catch {}

      // Retry schedule (milliseconds)
      const delays = justLoggedIn ? [0, 150, 350, 700, 1200] : [0, 150, 350];

      for (let i = 0; i < delays.length; i++) {
        if (delays[i]) await sleep(delays[i]);
        try {
          await getWhoAmIOnce(ctrl.signal);
          if (!cancelled) setState('authed');
          return;
        } catch {
          // swallow and continue
        }
      }

      if (!cancelled) setState('anon');
    };

    run();
    return () => {
      cancelled = true;
      try {
        ctrl.abort();
      } catch {}
    };
  }, [isDeptPortal]);

  // Never redirect while 'pending' — prevents bounce loops
  if (state === 'pending') return null;

  return state === 'authed' ? children : <Navigate to="/login" replace />;
}
